// Patient Records → Audit (KMC #26): the patient-level legal history.
//
// Reads GET /api/patients/:uhid/audit, which is gated on records:audit and
// itself logs that this trail was opened. Every row's actor, role, time and
// source come from the server's audit_log — nothing here is computed from the
// browser, and the raw stored details are one click away on every row so the
// wording on screen is never the only record of what happened.
//
// Rows written before JWT attribution (a free-text name, no user id) are
// shown as "unverified" rather than silently looking as trustworthy as the rest.
import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { keys } from '@/lib/queryKeys';
import { toCsv, downloadCsv } from '@/lib/csv';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { LoadingState } from '@/components/ui/States';

export interface AuditEntry {
  id: number;
  at: string;
  user_name: string | null;
  user_id: number | null;
  role: string | null;
  role_is_current: boolean;
  identity_verified: boolean;
  action: 'INSERT' | 'UPDATE' | 'DELETE' | 'ACCESS' | string;
  event: string | null;
  action_label: string;
  record_type: string;
  record_label: string;
  record_id: number | null;
  summary: string | null;
  reason: string | null;
  time_taken: string | null;
  time_entered: string | null;
  late_entry: boolean;
  source: string | null;
  source_label: string | null;
  branch_name: string | null;
  ip_address: string | null;
  user_agent: string | null;
  details: Record<string, unknown>;
}

interface AuditPage {
  patient: { id: number; uhid: string; name: string };
  entries: AuditEntry[];
  next_before: number | null;
}

// Changes only. Read (view) events are still written to audit_log by the
// server for legal tracking, but this screen deliberately does not list them.
const KIND = 'changes';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad2 = (n: number) => String(n).padStart(2, '0');
function fmt(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${pad2(d.getDate())}-${MONTHS[d.getMonth()]}-${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function actionVariant(e: AuditEntry): 'neutral' | 'info' | 'warn' | 'danger' | 'success' {
  if (e.action === 'ACCESS') return 'neutral';
  if (e.action === 'DELETE' || e.event === 'cancel') return 'danger';
  if (e.event === 'amend' || e.action === 'UPDATE') return 'warn';
  return 'success';
}

export function PatientAuditTab({ uhid }: { uhid: string }) {
  const [open, setOpen] = useState<number | null>(null);
  const q = useInfiniteQuery({
    queryKey: keys.patients.audit(uhid, KIND),
    queryFn: ({ pageParam }) => api.get<AuditPage>(`/patients/${encodeURIComponent(uhid)}/audit`, {
      query: { kind: KIND, limit: 100, before: pageParam ?? undefined },
    }),
    initialPageParam: null as number | null,
    getNextPageParam: last => last.next_before,
  });
  const entries = q.data?.pages.flatMap(p => p.entries) ?? [];

  const exportCsv = () => {
    const rows = entries.map(e => ({
      at: fmt(e.at), user: e.user_name ?? '', user_id: e.user_id ?? '', verified: e.identity_verified ? 'yes' : 'no',
      role: e.role ?? '', action: e.action_label, record: e.record_label, record_id: e.record_id ?? '',
      summary: e.summary ?? '', reason: e.reason ?? '', time_taken: fmt(e.time_taken), time_entered: fmt(e.time_entered),
      late: e.late_entry ? 'yes' : '', source: e.source_label ?? '', branch: e.branch_name ?? '', ip: e.ip_address ?? '',
    }));
    type Row = typeof rows[number];
    const cols: Array<[keyof Row, string]> = [
      ['at', 'Date & time'], ['user', 'User'], ['user_id', 'User ID'], ['verified', 'Identity verified'], ['role', 'Role'],
      ['action', 'Action'], ['record', 'Record'], ['record_id', 'Record ID'], ['summary', 'Detail'], ['reason', 'Reason'],
      ['time_taken', 'Time taken'], ['time_entered', 'Time entered'], ['late', 'Late entry'], ['source', 'Source'],
      ['branch', 'Branch'], ['ip', 'IP address'],
    ];
    downloadCsv(`audit-${uhid}-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(rows, cols));
  };

  return (
    <div className="fade-in">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">Legal audit trail — changes</div>
        <Button variant="ghost" size="sm" className="ml-auto" onClick={exportCsv} disabled={!entries.length}>
          Export CSV
        </Button>
      </div>
      <div className="mb-3 text-[11.5px] text-ink-muted">
        Every change to this patient&rsquo;s record, attributed to the signed-in user who made it.
      </div>

      {q.isLoading ? <LoadingState label="Loading audit trail…" />
        : q.isError ? (
          <div className="rounded bg-danger-soft px-3 py-2 text-[12.5px] text-danger-ink">
            {(q.error as Error)?.message || 'Could not load the audit trail.'}
          </div>
        )
        : !entries.length ? <div className="py-8 text-center text-[13px] text-ink-muted">No audited activity for this patient yet.</div>
        : (
          <div className="-mx-1 overflow-x-auto px-1">
            <table className="w-full min-w-[760px] text-[12.5px]">
              <thead>
                <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-wide text-ink-muted">
                  <th className="py-1.5 pr-3 font-semibold">Date &amp; time</th>
                  <th className="py-1.5 pr-3 font-semibold">User</th>
                  <th className="py-1.5 pr-3 font-semibold">Role</th>
                  <th className="py-1.5 pr-3 font-semibold">Action</th>
                  <th className="py-1.5 pr-3 font-semibold">Record</th>
                  <th className="py-1.5 pr-3 font-semibold">Source</th>
                  <th className="py-1.5 font-semibold" />
                </tr>
              </thead>
              <tbody>
                {entries.map(e => (
                  <FragmentRow key={e.id} e={e} open={open === e.id} onToggle={() => setOpen(open === e.id ? null : e.id)} />
                ))}
              </tbody>
            </table>
            {q.hasNextPage && (
              <div className="mt-3 flex justify-center">
                <Button variant="ghost" size="sm" onClick={() => q.fetchNextPage()} loading={q.isFetchingNextPage}>
                  Load older activity
                </Button>
              </div>
            )}
          </div>
        )}
    </div>
  );
}

function FragmentRow({ e, open, onToggle }: { e: AuditEntry; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr className="border-b border-border/60 align-top">
        <td className="whitespace-nowrap py-1.5 pr-3 tabular-nums">{fmt(e.at)}</td>
        <td className="py-1.5 pr-3">
          {e.user_name || '—'}
          {!e.identity_verified && (
            <span className="ml-1" title="Recorded before sign-in attribution; the name was not verified against a user account.">
              <Badge variant="caution">unverified</Badge>
            </span>
          )}
        </td>
        <td className="py-1.5 pr-3 text-ink-muted" title={e.role_is_current ? 'Role not recorded at the time; showing the account’s current role.' : undefined}>
          {e.role || '—'}{e.role_is_current ? '*' : ''}
        </td>
        <td className="py-1.5 pr-3">
          <Badge variant={actionVariant(e)}>{e.action_label}</Badge>
          {e.summary && <div className="mt-0.5 text-[11.5px] text-ink-muted">{e.summary}</div>}
          {e.late_entry && (
            <div className="mt-0.5 text-[11px] text-warn-ink">
              Taken {fmt(e.time_taken)} · Entered {fmt(e.time_entered)} · Late entry
            </div>
          )}
          {e.reason && <div className="mt-0.5 text-[11.5px] text-ink-muted">Reason: {e.reason}</div>}
        </td>
        <td className="py-1.5 pr-3">{e.record_label}{e.record_id != null ? <span className="text-ink-muted"> #{e.record_id}</span> : null}</td>
        <td className="py-1.5 pr-3 text-ink-muted">
          {e.source_label || '—'}{e.branch_name ? <div className="text-[11px]">{e.branch_name}</div> : null}
        </td>
        <td className="py-1.5 text-right">
          <button type="button" onClick={onToggle} className="text-[11.5px] font-semibold text-teal underline-offset-2 hover:underline">
            {open ? 'Hide' : 'Details'}
          </button>
        </td>
      </tr>
      {open && (
        <tr className="border-b border-border/60">
          <td colSpan={7} className="bg-muted/40 px-3 py-2 text-[11.5px]">
            <div className="mb-1 text-ink-muted">
              {e.action} on {e.record_type}{e.record_id != null ? ` #${e.record_id}` : ''}
              {e.user_id != null && <> · user id {e.user_id}</>}
              {e.ip_address && <> · from {e.ip_address}</>}
              {e.user_agent && <> · {e.user_agent}</>}
            </div>
            <pre className="max-h-60 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px]">
              {JSON.stringify(e.details, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

export default PatientAuditTab;
